// FLAGS: personal-data
// The app's single Application Insights instance, and the W3C traceparent / Request-Id pair that
// axiosInterceptors attaches to every API call so a browser error joins up with the API's server-side trace.
import { APPLICATION_INSIGHTS_APP_NAME } from "@src/constants";
import initializeAppInsights from "@openup/shared/helpers/appInsights/AppInsights";
import { env } from "@src/env";

interface TraceHeaders {
  traceparent: string;
  requestId: string;
}

const generateSpanId = (): string => {
  const array = new Uint8Array(8);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join(
    "",
  );
};

const ai = initializeAppInsights(
  env.REACT_APP_APPINSIGHTS_CONNECTION_STRING,
  APPLICATION_INSIGHTS_APP_NAME,
  env.REACT_APP_VERSION,
  "",
  true,
);

export const { appInsights, reactPlugin } = ai;

// FLAGS: personal-data
// Stamps the staff user's id onto every subsequent telemetry item and, because the SDK's third argument is
// storeInCookie, persists it in a browser cookie that outlives the tab. The paired clear runs on logout via
// clearUserFromStorage; drop that call and the next operator's telemetry is attributed to the previous one.
export const setAuthenticatedUserContext = (userId: string) => {
  ai.appInsights.setAuthenticatedUserContext(userId, "", true);
};

export const clearAuthenticatedUserContext = () => {
  appInsights.clearAuthenticatedUserContext();
};

const isValidSpanId = (id: string): boolean => /^[0-9a-f]{16}$/.test(id);

const getSpanId = (): string => {
  const parentId = appInsights.context?.telemetryTrace?.parentID;
  return parentId && isValidSpanId(parentId) ? parentId : generateSpanId();
};

export const getTraceHeaders = (): TraceHeaders | undefined => {
  const traceId = appInsights.context?.telemetryTrace?.traceID;
  if (!traceId) return undefined;

  const spanId = getSpanId();
  return {
    traceparent: `00-${traceId}-${spanId}-01`,
    requestId: `|${traceId}.${spanId}.`,
  };
};
